import {
  AbsolutePath,
  CancellablePromise,
  compareSchemaPaths,
  DisplayStatus,
  FormManager,
  Info,
  Path,
  SealedValidationIssue,
  StateEvent,
  ValueEvent,
} from "@kform/core";
import * as React from "react";

import { binarySearch } from "../utils/binarySearch";
import { ignorePromiseCancellationException } from "../utils/ignorePromiseCancellationException";
import { useFormManager } from "./useFormManager";
import { useResolvedPath } from "./useResolvedPath";

/**
 * Options available to the {@link useIssuesTracker} hook.
 */
export interface IssuesTrackerOptions {
  /**
   * Required if no form context is in scope.
   *
   * If a form context is in scope and this value is also provided, then the
   * provided form manager will be used, in which case the current path of the
   * form context is ignored.
   */
  formManager?: FormManager;
  /**
   * Function which may be provided to specify the order of the resulting
   * issues.
   *
   * It receives the paths of the issues being compared and should return a
   * number in typical
   * [Array.prototype.sort#compareFn](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#comparefn)
   * fashion to determine the order of the issues.
   *
   * The default ordering is based on the order in which fields were declared
   * within the form schema. If this function is provided, and `0` is returned,
   * then the default schema-based ordering will be used for disambiguation.
   *
   * Note that this function should **not** change on every render, as any
   * change to this function will cause the tracker to "restart".
   * @param path1 First path being compared.
   * @param path2 Second path being compared.
   * @returns Positive number if `path1 > path2`, negative number if
   * `path2 > path1`, `0` otherwise.
   */
  issuesOrderCompareFn?: (path1: AbsolutePath, path2: AbsolutePath) => number;
  /**
   * Whether to enable validation issues tracking.
   * @default true
   */
  enabled?: boolean;
}

/**
 * Result of the {@link useIssuesTracker} hook, containing the validation issues
 * of the tracked values (when tracking and initialised).
 */
export type IssuesTrackerResult =
  | UninitializedTrackedValidationIssues
  | InitializedTrackedValidationIssues;

/**
 * Base representation of the result of the {@link useIssuesTracker}
 * hook.
 */
export interface BaseIssuesTrackerResult {
  /**
   * Whether the tracking of validation issues has been initialised.
   */
  initialized: boolean;
  /**
   * Validation information about the values with issues.
   */
  info?: LocatedValidationInfo[];
  /**
   * Total number of errors.
   */
  errors?: number;
  /**
   * Total number of warnings.
   */
  warnings?: number;
}

/**
 * Result of the {@link useIssuesTracker} hook while initialising the
 * tracking of validation issues.
 */
export interface UninitializedTrackedValidationIssues
  extends BaseIssuesTrackerResult {
  initialized: false;
  info?: undefined;
  errors?: undefined;
  warnings?: undefined;
}

/**
 * Result of the {@link useIssuesTracker} hook while tracking
 * validation issues.
 */
export interface InitializedTrackedValidationIssues
  extends BaseIssuesTrackerResult {
  initialized: true;
  info: LocatedValidationInfo[];
  errors: number;
  warnings: number;
}

/**
 * Information about the issues of a certain value.
 */
export interface LocatedValidationInfo {
  /**
   * Path of the value with issues.
   */
  path: AbsolutePath;
  /**
   * Issues of the value (the array is guaranteed to not be empty).
   */
  issues: SealedValidationIssue[];
  /**
   * Local display status of the value (ignores issues of descendants).
   */
  localDisplayStatus: Exclude<DisplayStatus, "valid">;
}

/**
 * Internal validation state of a value.
 */
type ValidationState = Pick<Info, "touched" | "issues">;

/**
 * Hook used to keep track of validation issues of values at paths matching
 * {@link path}.
 * @param path Path of values for which to keep track of validation issues.
 * Defaults to `/**`.
 * @param issuesTrackerOptions Available options.
 * @returns Object containing information on issues of values at paths matching
 * {@link path}.
 */
export function useIssuesTracker(
  path: Path | string = Path.CURRENT_DEEP,
  {
    formManager: formManagerOption,
    issuesOrderCompareFn,
    enabled = true,
  }: IssuesTrackerOptions = {},
): IssuesTrackerResult {
  const formManager = useFormManager(formManagerOption);
  const absolutePath = useResolvedPath(
    path,
    formManagerOption != null ? AbsolutePath.ROOT : undefined,
  );
  const formSchema = React.useMemo(() => formManager.schema(), [formManager]);

  const compareInfo = React.useCallback(
    (info1: LocatedValidationInfo, info2: LocatedValidationInfo): number =>
      issuesOrderCompareFn?.(info1.path, info2.path) ||
      compareSchemaPaths(formSchema, info1.path, info2.path),
    [formSchema, issuesOrderCompareFn],
  );

  const [trackedState, setTrackedState] = React.useState<IssuesTrackerResult>({
    initialized: false,
  });

  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    let cleanedUp = false;
    let unsubscribe: (() => CancellablePromise<void>) | undefined = undefined;
    const stateByPath = new Map<string, ValidationState>();
    let trackedInfo: LocatedValidationInfo[] = [];
    let errors = 0;
    let warnings = 0;

    const infoPromise = formManager
      .info(absolutePath, async (infoIterable) => {
        if (cleanedUp) {
          return;
        }

        // Initialise tracked state
        for await (const info of infoIterable) {
          const newState = { touched: info.touched, issues: info.issues };
          stateByPath.set(info.path.toString(), newState);
          const newInfo = validationStateToValidationInfo(info.path, newState);
          if (newInfo !== undefined) {
            trackedInfo.push(newInfo);
            const counts = countIssueSeverities(info.issues);
            errors += counts.errors;
            warnings += counts.warnings;
          }
        }
        setTrackedState({
          initialized: true,
          info: trackedInfo.sort(compareInfo),
          errors,
          warnings,
        });

        unsubscribe = await formManager.subscribe(absolutePath, (event) => {
          // Unsubscribing is asynchronous, so it's possible that an event is
          // emitted after cleaning up, but before unsubscribing, which we
          // should ignore
          if (cleanedUp) {
            return;
          }

          const pathStr = event.path.toString();
          const oldState = stateByPath.get(pathStr);
          let newState: ValidationState | undefined = oldState;

          if (event instanceof ValueEvent.Init) {
            newState = { touched: false, issues: [] };
          } else if (event instanceof ValueEvent.Destroy) {
            newState = undefined;
          } else if (event instanceof StateEvent.ValidationChange) {
            newState = {
              touched: oldState?.touched ?? false,
              issues: event.issues,
            };
          } else if (event instanceof StateEvent.TouchedChange) {
            newState = {
              touched: event.status,
              issues: oldState?.issues ?? [],
            };
          }

          if (oldState === newState) {
            return;
          }

          // Update state by path map
          if (!newState) {
            stateByPath.delete(pathStr);
          } else {
            stateByPath.set(pathStr, newState);
          }

          // Update tracked state when something actually changes
          const oldCountedIssues = oldState?.touched ? oldState.issues : [];
          const newCountedIssues = newState?.touched ? newState.issues : [];
          if (!equalIssues(oldCountedIssues, newCountedIssues)) {
            trackedInfo = updateTrackedInfo(
              trackedInfo,
              event.path,
              newState,
              compareInfo,
            );
            const oldCounts = countIssueSeverities(oldCountedIssues);
            const newCounts = countIssueSeverities(newCountedIssues);
            errors += newCounts.errors - oldCounts.errors;
            warnings += newCounts.warnings - oldCounts.warnings;

            setTrackedState({
              initialized: true,
              info: trackedInfo,
              errors,
              warnings,
            });
          }
        });
      })
      .catch(ignorePromiseCancellationException);

    return () => {
      cleanedUp = true;
      infoPromise.cancel(
        `Clean up 'useEffect' access to info of '${absolutePath.toString()}'.`,
      );
      void unsubscribe?.();
      setTrackedState({ initialized: false });
    };
  }, [absolutePath, compareInfo, enabled, formManager]);

  return trackedState;
}

function validationStateToValidationInfo(
  path: AbsolutePath,
  state: ValidationState | undefined,
): LocatedValidationInfo | undefined {
  return state?.touched && state.issues.length > 0
    ? {
        path,
        issues: state.issues,
        localDisplayStatus: state.issues.some(
          (issue) => issue.severity === "error",
        )
          ? "error"
          : "warning",
      }
    : undefined;
}

function updateTrackedInfo(
  trackedInfo: LocatedValidationInfo[],
  path: AbsolutePath,
  newState: ValidationState | undefined,
  compareInfo: (
    info1: LocatedValidationInfo,
    info2: LocatedValidationInfo,
  ) => number,
) {
  const infoOfPath = { path } as LocatedValidationInfo;
  const index = binarySearch(trackedInfo, infoOfPath, compareInfo);
  const newInfo = validationStateToValidationInfo(path, newState);

  if (index < 0 && newInfo === undefined) {
    return trackedInfo;
  }

  // TODO: Use `toSpliced` and `with` instead of slicing first once browser
  //  support is better
  const result = trackedInfo.slice();
  if (index >= 0) {
    if (newInfo === undefined) {
      result.splice(index, 1);
    } else {
      result[index] = newInfo;
    }
  } else {
    result.splice(-index - 1, 0, newInfo!);
  }
  return result;
}

function countIssueSeverities(issues: SealedValidationIssue[]): {
  errors: number;
  warnings: number;
} {
  let errors = 0;
  let warnings = 0;
  for (const issue of issues) {
    if (issue.severity === "error") {
      ++errors;
    } else {
      ++warnings;
    }
  }
  return { errors, warnings };
}

function equalIssues(
  issues1: SealedValidationIssue[],
  issues2: SealedValidationIssue[],
): boolean {
  return (
    issues1.length === issues2.length &&
    issues1.every((issue, i) => issue.equals(issues2[i]))
  );
}
